import { Row } from '@tanstack/react-table';
import { NoddiIcon } from 'noddi-ui-common';
import type { MouseEvent } from 'react';
import {
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger
} from '../../../../base/context-menu';
import { TableContextMenuTranslations } from '../tableTranslationTypes';
import { TableAction, TableActionItem } from '../types';
import { getActionTypeIcon } from '../useGetProcessedColumns';

type Props<TData> = {
  row: Row<TData>;
  actions?: TableAction<TData>[];
  texts: TableContextMenuTranslations;
  /** Omitted when the host hides "Copy row id". */
  onCopyRowId?: (row: Row<TData>) => void;
};

const renderActionItem = <TData,>(item: TableActionItem<TData>, row: Row<TData>, key: string) => {
  const disabled = item.disabled?.(row.original) ?? false;

  if (item.items?.length) {
    return (
      <ContextMenuSub key={key}>
        <ContextMenuSubTrigger disabled={disabled}>{item.label}</ContextMenuSubTrigger>
        <ContextMenuSubContent>
          {item.items.map((child, index) => renderActionItem(child, row, `${key}-${child.label}-${index}`))}
        </ContextMenuSubContent>
      </ContextMenuSub>
    );
  }

  if (item.checked) {
    return (
      <ContextMenuCheckboxItem
        key={key}
        checked={item.checked(row.original)}
        disabled={disabled}
        onCheckedChange={() => item.onClick?.(row.original)}
      >
        {item.label}
      </ContextMenuCheckboxItem>
    );
  }

  return (
    <ContextMenuItem
      key={key}
      disabled={disabled}
      onClick={(event: MouseEvent) => {
        // preventDefault would cancel the menu's own select handler, so the menu
        // stays open and leaves the body scroll lock in place.
        event.stopPropagation();
        item.onClick?.(row.original);
      }}
    >
      {item.label}
    </ContextMenuItem>
  );
};

export const TableRowContextMenuContent = <TData,>({ row, actions, texts, onCopyRowId }: Props<TData>) => (
  <ContextMenuContent>
    {onCopyRowId && (
      <ContextMenuItem onClick={() => onCopyRowId(row)}>
        <NoddiIcon name='Copy' size='small' className='mr-2' />
        {texts.copyRowId}
      </ContextMenuItem>
    )}
    {actions?.length ? (
      <>
        {onCopyRowId && <ContextMenuSeparator />}
        {actions.map((action, index) => {
          const key = `${action.actionType}-${action.label}-${index}`;
          const disabled = action.disabled?.(row.original) ?? false;

          if (action.items?.length) {
            return (
              <ContextMenuSub key={key}>
                <ContextMenuSubTrigger disabled={disabled}>
                  <span className='mr-2 size-4'>{getActionTypeIcon(action.actionType)}</span>
                  {action.label}
                </ContextMenuSubTrigger>
                <ContextMenuSubContent>
                  {action.items.map((item, itemIndex) =>
                    renderActionItem(item, row, `${key}-${item.label}-${itemIndex}`)
                  )}
                </ContextMenuSubContent>
              </ContextMenuSub>
            );
          }

          return (
            <ContextMenuItem
              key={key}
              disabled={disabled}
              onClick={(event: MouseEvent) => {
                event.stopPropagation();
                action.onClick?.(row.original);
              }}
            >
              <span className='mr-2 size-4'>{getActionTypeIcon(action.actionType)}</span>
              {action.label}
            </ContextMenuItem>
          );
        })}
      </>
    ) : null}
  </ContextMenuContent>
);
